import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

type GetCoursePrice = () => number;

type GetExistingCouponValues = () => string[];

const hasOnlySpaces = (value: string): boolean => value.trim().length === 0;

const titleAllowedCharsRe = /^[A-Za-z0-9 &-]+(?:\s[A-Za-z0-9 &-]+)*$/;
// Note: This RE ensures:
// - only allowed chars (letters, numbers, spaces, hyphen, ampersand)
// - not only spaces (we check separately)
// - no leading/trailing spaces are allowed by trimming logic requirement.

const codeAllowedCharsRe = /^[A-Za-z0-9]+$/;

export const couponTitleValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;

    const str = String(raw);

    // Do not allow only spaces
    if (hasOnlySpaces(str)) return { couponTitleRequired: true };

    // Trim leading/trailing spaces
    if (str.trim() !== str) return { couponTitleTrim: true };

    const trimmed = str.trim();

    if (trimmed.length < 2) return { couponTitleMinLength: { requiredLength: 2, actualLength: trimmed.length } };
    if (trimmed.length > 100) return { couponTitleMaxLength: { requiredLength: 100, actualLength: trimmed.length } };

    if (!titleAllowedCharsRe.test(trimmed)) return { couponTitleAllowedChars: true };

    return null;
  };
};

export const couponCodeValidator = (opts?: {
  preferUppercase?: boolean;
}): ValidatorFn => {
  const { preferUppercase = true } = opts ?? {};

  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;

    let str = String(raw);

    // No leading/trailing spaces and no spaces allowed at all
    if (str.includes(' ')) return { couponCodeSpacesNotAllowed: true };
    if (str.trim() !== str) return { couponCodeTrim: true };

    if (preferUppercase) {
      const upper = str.toUpperCase();
      // If user typed lower-case, we normalize by updating control (without making it impossible to validate)
      if (upper !== str) {
        control.setValue(upper, { emitEvent: false });
        str = upper;
      }
    }

    const trimmed = str.trim();
    if (trimmed.length < 3) return { couponCodeMinLength: { requiredLength: 3, actualLength: trimmed.length } };
    if (trimmed.length > 20) return { couponCodeMaxLength: { requiredLength: 20, actualLength: trimmed.length } };

    if (!codeAllowedCharsRe.test(trimmed)) return { couponCodeAllowedChars: true };

    return null;
  };
};

export const couponDiscountAmountValidator = (opts: {
  getCoursePrice: GetCoursePrice;
  getDiscountType: () => '$' | '%';
}): ValidatorFn => {
  const { getCoursePrice, getDiscountType } = opts;

  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;

    const str = String(raw);

    // Only digits
    if (!/^\d+$/.test(str)) return { couponDiscountDigitsOnly: true };

    // Max 7 digits
    if (str.trim().length > 7) return { couponDiscountMaxLength: { requiredLength: 7, actualLength: str.trim().length } };

    const n = Number(str);
    if (!Number.isFinite(n)) return { couponDiscountDigitsOnly: true };

    // > 0
    if (n <= 0) return { couponDiscountMinValue: { min: 1, actual: n } };

    const coursePrice = Math.max(0, Number(getCoursePrice() || 0));

    const discountType = getDiscountType();

    if (discountType === '%') {
      // percentage cannot exceed 100%
      if (n > 100) return { couponDiscountPercentageMax: { max: 100, actual: n } };
      return null;
    }

    // fixed amount cannot exceed course price
    if (coursePrice > 0 && n > coursePrice) return { couponDiscountExceedsCoursePrice: { max: coursePrice, actual: n } };

    return null;
  };
};

export const couponNotPastDateValidator = (todayIsoYmd: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (!raw) return null;
    const value = String(raw);

    // Expect ISO date yyyy-mm-dd from input[type=date]
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return { couponExpiryInvalidDate: true };

    if (value < todayIsoYmd) return { couponExpiryPastDate: true };

    return null;
  };
};

// from_date: not in the past (today or future)
export const couponFromNotPastDateValidator = (todayIsoYmd: string): ValidatorFn => {
  return couponNotPastDateValidator(todayIsoYmd);
};

// Cross-field validator: to_date must be on/after from_date
export const couponToOnOrAfterFromDateValidator = (opts: {
  fromControlName: string;
  toControlName: string;
  invalidKey?: string;
}): ValidatorFn => {
  const {
    fromControlName,
    toControlName,
    invalidKey = 'couponToBeforeFromDate'
  } = opts;

  return (control: AbstractControl): ValidationErrors | null => {
    const group = control as any;
    const fromCtrl: AbstractControl | null = group?.get?.(fromControlName) ?? null;
    const toCtrl: AbstractControl | null = group?.get?.(toControlName) ?? null;
    if (!fromCtrl || !toCtrl) return null;

    const fromRaw = fromCtrl.value;
    const toRaw = toCtrl.value;

    // Let required/format validators handle empties/invalid formats.
    if (!fromRaw || !toRaw) return null;

    const fromVal = String(fromRaw);
    const toVal = String(toRaw);

    if (!/^\d{4}-\d{2}-\d{2}$/.test(fromVal) || !/^\d{4}-\d{2}-\d{2}$/.test(toVal)) {
      return null;
    }

    const isValidRange = toVal >= fromVal;

    if (isValidRange) {
      // Clear only our range error key while preserving others.
      const prev = toCtrl.errors ?? {};
      if (prev[invalidKey]) {
        const { [invalidKey]: _, ...rest } = prev;
        toCtrl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    }

    const prev = toCtrl.errors ?? {};
    toCtrl.setErrors({ ...prev, [invalidKey]: true });
    return { [invalidKey]: true };
  };
};

export const couponUsagePerUserValidator = (opts: {
  max?: number;
}): ValidatorFn => {
  const { max = 999 } = opts;

  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;

    const str = String(raw);

    // Only digits
    if (!/^\d+$/.test(str)) return { couponUsageDigitsOnly: true };

    const n = Number(str);
    if (!Number.isFinite(n)) return { couponUsageDigitsOnly: true };

    if (n <= 0) return { couponUsageMinValue: { min: 1, actual: n } };

    if (n > max) return { couponUsageMaxValue: { max, actual: n } };

    return null;
  };
};



export const couponUniqueValidator = (opts: {
  getExistingValues: GetExistingCouponValues;
  normalize: (v: string) => string;
  errorKey: string;
}): ValidatorFn => {
  const { getExistingValues, normalize, errorKey } = opts;

  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;

    const value = normalize(String(raw));
    if (!value) return null;

    const existing = getExistingValues().map(normalize);
    if (existing.includes(value)) {
      return { [errorKey]: true };
    }

    return null;
  };
};

